import type { IApiColumnConfig } from '@/creditflowresearch/domain/vo/Bondscreener';
import type { IColumnConfigBase, IColumnDefinition, IColumnRendererEntry } from './types';
import columnRenderStrategyMap from './columnRenderStrategyMap';

export const resolveColumnFromCustomCallback = <TRow>(
  apiCol: IApiColumnConfig,
  localEntry: IColumnRendererEntry<TRow>,
): IColumnDefinition => ({
  name: apiCol.id,
  dataType: localEntry.dataType as IColumnDefinition['dataType'],
  title: apiCol.shortName ?? apiCol.label,
  tooltipContent: apiCol.label,
  sortable: apiCol.sortable,
  width: localEntry.width ?? 100,
  minWidth: localEntry.minWidth ?? 80,
  maxWidth: localEntry.maxWidth ?? 200,
  tooltipMaxWidth: localEntry.tooltipMaxWidth,
  className: localEntry.className,
  customAlign: localEntry.customAlign,
  cellCallback: localEntry.cellCallback!,
});

export const resolveColumnFromFactory = <TRow>(
  apiCol: IApiColumnConfig,
  localEntry: IColumnRendererEntry<TRow> | undefined,
  dataField: string,
): IColumnDefinition => {
  const strategyKey = localEntry?.dataType ?? 'text';
  const factory = columnRenderStrategyMap[strategyKey] ?? columnRenderStrategyMap.text;

  const definition = factory({
    id: dataField,
    dataField,
    shortName: apiCol.shortName ?? apiCol.label,
    fullName: apiCol.label,
    ...(localEntry ?? {}),
  } as IColumnConfigBase & Record<string, unknown>);

  return {
    ...definition,
    name: apiCol.id,
    title: apiCol.shortName ?? apiCol.label,
    tooltipContent: apiCol.label,
    sortable: apiCol.sortable,
    tooltipMaxWidth: localEntry?.tooltipMaxWidth,
    className: localEntry?.className,
    customAlign: localEntry?.customAlign,
  };
};

export const resolveColumnFromApiConfig = <TRow>(
  apiCol: IApiColumnConfig,
  localEntry: IColumnRendererEntry<TRow> | undefined,
  dataField: string,
): IColumnDefinition => {
  const definition = localEntry?.cellCallback
    ? resolveColumnFromCustomCallback(apiCol, localEntry)
    : resolveColumnFromFactory(apiCol, localEntry, dataField);

  return { ...definition, ...localEntry?.overrides };
};
